import React, { useState, useEffect } from "react";
import { FiPlus, FiTrash2, FiUpload, FiX, FiImage, FiChevronDown } from "react-icons/fi";
import PageView from "../../../../../../components/commonComponents/Viewpage";
import { useGetCategoriesQuery, useCreateProductMutation } from "../../../../../../store/api/VendorcatalogApi";
import { useGetVendorWarehousesQuery } from "../../../../../../store/api/VendorWarehouseApi";

function ProductCreate() {
  const { data: categoriesData, isLoading: categoriesLoading } = useGetCategoriesQuery();
  const { data: warehousesData, isLoading: warehousesLoading } = useGetVendorWarehousesQuery();
  const [createProduct, { isLoading: creatingProduct }] = useCreateProductMutation();

  const [product, setProduct] = useState({
    title: '',
    description: '',
    categories: [],
    primary_category: '',
    main_image: null,
    variants: [
      {
        attributes: '',
        title: '',
        gst: '',
        mrp: '',
        price: '',
        discount: '',
        stock: '',
        weight: '',
        length: '',
        width: '',
        height: '',
        currency: 'INR',
        warehouse_id: '', // Added warehouse_id field
        images: [{ image: null, alt_text: '' }]
      }
    ]
  });

  const [errors, setErrors] = useState({});
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Get primary categories (parent: null)
  const primaryCategories = categoriesData?.data?.filter(cat => cat.parent === null) || [];

  // Get warehouses
  const warehouses = warehousesData || [];

  // Get child categories based on selected primary categories
  const getChildCategories = () => {
    if (!product.categories.length) return [];

    const childCats = [];
    product.categories.forEach(catId => {
      const children = categoriesData?.data?.filter(childCat => childCat.parent === parseInt(catId)) || [];
      childCats.push(...children);
    });
    return childCats;
  };

  const childCategories = getChildCategories();

  // Generate alt text from filename
  const generateAltTextFromFilename = (filename) => {
    if (!filename) return '';

    // Remove file extension
    let altText = filename.replace(/\.[^/.]+$/, "");

    // Replace special characters with spaces
    altText = altText.replace(/[_-]/g, " ");

    // Capitalize first letter of each word
    altText = altText.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });

    // Remove multiple spaces
    altText = altText.replace(/\s+/g, " ").trim();

    return altText;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePrimaryCategoryChange = (categoryId) => {
    const newPrimaryCategory = categoryId.toString();

    setProduct(prev => ({
      ...prev,
      primary_category: newPrimaryCategory,
      // Auto-add primary category to categories if not already present
      categories: prev.categories.includes(newPrimaryCategory)
        ? prev.categories
        : [...prev.categories, newPrimaryCategory]
    }));

    if (errors.primary_category) {
      setErrors(prev => ({ ...prev, primary_category: '' }));
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setProduct(prev => {
      const currentCategories = [...prev.categories];
      const categoryIndex = currentCategories.indexOf(categoryId.toString());

      if (categoryIndex > -1) {
        currentCategories.splice(categoryIndex, 1);
      } else {
        currentCategories.push(categoryId.toString());
      }

      return {
        ...prev,
        categories: currentCategories
      };
    });
  };

  const isCategorySelected = (categoryId) => {
    return product.categories.includes(categoryId.toString());
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, main_image: 'Please select a valid image file' }));
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, main_image: 'Image size should be less than 5MB' }));
        return;
      }
      setProduct(prev => ({
        ...prev,
        main_image: file
      }));
      setErrors(prev => ({ ...prev, main_image: '' }));
    }
  };

  const handleVariantChange = (variantIndex, field, value) => {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex ? { ...variant, [field]: value } : variant
      )
    }));
  };

  const handleVariantImageChange = (variantIndex, imageIndex, field, value, filename = '') => {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex
          ? {
            ...variant,
            images: variant.images.map((img, imgIndex) => {
              if (imgIndex === imageIndex) {
                const updatedImage = { ...img, [field]: value };

                // Auto-generate alt text when image is selected and alt_text is empty
                if (field === 'image' && value && (!img.alt_text || img.alt_text === '')) {
                  const altText = generateAltTextFromFilename(filename || value.name);
                  updatedImage.alt_text = altText;
                }

                return updatedImage;
              }
              return img;
            })
          }
          : variant
      )
    }));
  };

  const addVariant = () => {
    setProduct(prev => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          attributes: '',
          title: '',
          gst: '',
          mrp: '',
          price: '',
          discount: '',
          stock: '',
          weight: '',
          length: '',
          width: '',
          height: '',
          currency: 'INR',
          warehouse_id: '', // Initialize with empty warehouse
          images: [{ image: null, alt_text: '' }]
        }
      ]
    }));
  };

  const removeVariant = (variantIndex) => {
    if (product.variants.length > 1) {
      setProduct(prev => ({
        ...prev,
        variants: prev.variants.filter((_, index) => index !== variantIndex)
      }));
    }
  };

  const addVariantImage = (variantIndex) => {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex
          ? {
            ...variant,
            images: [...variant.images, { image: null, alt_text: '' }]
          }
          : variant
      )
    }));
  };

  const removeVariantImage = (variantIndex, imageIndex) => {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex
          ? {
            ...variant,
            images: variant.images.filter((_, imgIndex) => imgIndex !== imageIndex)
          }
          : variant
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!product.title.trim()) newErrors.title = 'Product title is required';
    if (!product.primary_category) newErrors.primary_category = 'Primary category is required';
    if (product.categories.length === 0) newErrors.categories = 'At least one category is required';
    if (!product.main_image) newErrors.main_image = 'Main product image is required';

    // Validate variants
    product.variants.forEach((variant, index) => {
      if (!variant.title.trim()) newErrors[`variant_${index}_title`] = 'Variant title is required';
      if (!variant.price) newErrors[`variant_${index}_price`] = 'Selling price is required';
      if (!variant.stock) newErrors[`variant_${index}_stock`] = 'Stock quantity is required';
      if (!variant.warehouse_id) newErrors[`variant_${index}_warehouse`] = 'Warehouse is required'; // Validate warehouse

      // Validate variant images
      variant.images.forEach((image, imgIndex) => {
        if (!image.image) newErrors[`variant_${index}_image_${imgIndex}`] = 'Variant image is required';
        if (!image.alt_text.trim()) newErrors[`variant_${index}_alt_${imgIndex}`] = 'Alt text is required';
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formData = new FormData();

    // Basic product fields
    formData.append('title', product.title);
    formData.append('description', product.description);
    formData.append('primary_category', product.primary_category);

    // Categories
    product.categories.forEach(category => {
      formData.append('categories', category);
    });

    // Main image
    if (product.main_image) {
      formData.append('main_image', product.main_image);
    }

    // Variants
    product.variants.forEach((variant, variantIndex) => {
      formData.append(`variants[${variantIndex}][attributes]`, variant.attributes);
      formData.append(`variants[${variantIndex}][title]`, variant.title);
      formData.append(`variants[${variantIndex}][gst]`, variant.gst || '0');
      formData.append(`variants[${variantIndex}][mrp]`, variant.mrp || '0');
      formData.append(`variants[${variantIndex}][price]`, variant.price);
      formData.append(`variants[${variantIndex}][discount]`, variant.discount || '0');
      formData.append(`variants[${variantIndex}][stock]`, variant.stock);
      formData.append(`variants[${variantIndex}][weight]`, variant.weight || '0');
      formData.append(`variants[${variantIndex}][length]`, variant.length || '0');
      formData.append(`variants[${variantIndex}][width]`, variant.width || '0');
      formData.append(`variants[${variantIndex}][height]`, variant.height || '0');
      formData.append(`variants[${variantIndex}][currency]`, variant.currency);
      formData.append(`variants[${variantIndex}][warehouse_id]`, variant.warehouse_id); // Add warehouse_id

      // Variant images
      variant.images.forEach((image, imageIndex) => {
        if (image.image) {
          formData.append(`variants[${variantIndex}][images][${imageIndex}][image]`, image.image);
        }
        formData.append(`variants[${variantIndex}][images][${imageIndex}][alt_text]`, image.alt_text || '');
      });
    });

    try {
      const result = await createProduct(formData).unwrap();

      if (result.success) {
        alert('Product created successfully!');
        // Reset form
        setProduct({
          title: '',
          description: '',
          categories: [],
          primary_category: '',
          main_image: null,
          variants: [
            {
              attributes: '',
              title: '',
              gst: '',
              mrp: '',
              price: '',
              discount: '',
              stock: '',
              weight: '',
              length: '',
              width: '',
              height: '',
              currency: 'INR',
              warehouse_id: '', // Reset warehouse_id
              images: [{ image: null, alt_text: '' }]
            }
          ]
        });
        setErrors({});
      } else {
        alert('Error creating product: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Error creating product. Please try again.');
    }
  };

  // Get selected category names for display
  const getSelectedCategoryNames = () => {
    return product.categories.map(catId => {
      const category = categoriesData?.data?.find(cat => cat.id.toString() === catId.toString());
      return category?.name || '';
    }).filter(name => name !== '');
  };

  if (categoriesLoading || warehousesLoading) {
    return (
      <PageView title="Create Product" closeTo="/dashboard">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading categories and warehouses...</div>
        </div>
      </PageView>
    );
  }

  return (
    <PageView title="Create Product" closeTo="/dashboard">
      <div className="w-full mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Product Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Basic Information</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={product.title}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.title ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter product title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={product.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border h-[250px] border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter product description"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Category *
                  </label>
                  <select
                    name="primary_category"
                    value={product.primary_category}
                    onChange={(e) => handlePrimaryCategoryChange(e.target.value)}
                    required
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.primary_category ? 'border-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">Select primary category</option>
                    {primaryCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.primary_category && (
                    <p className="mt-1 text-sm text-red-600">{errors.primary_category}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Selecting a primary category will automatically add it to the categories list
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categories *
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${errors.categories ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                      <span className="text-left truncate">
                        {product.categories.length > 0
                          ? `${getSelectedCategoryNames().join(', ')}`
                          : 'Select categories'
                        }
                      </span>
                      <FiChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showCategoryDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 space-y-2">
                          {/* Show only primary categories in dropdown */}
                          {primaryCategories.map(category => (
                            <label key={category.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isCategorySelected(category.id)}
                                onChange={() => handleCategoryToggle(category.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="font-medium">{category.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.categories && (
                    <p className="mt-1 text-sm text-red-600">{errors.categories}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Select one or more primary categories
                  </p>

                  {/* Show child categories of selected primary categories */}
                  {childCategories.length > 0 && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Available Sub-categories
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {childCategories.map(childCat => (
                          <label key={childCat.id} className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isCategorySelected(childCat.id)}
                              onChange={() => handleCategoryToggle(childCat.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm">{childCat.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Main Product Image *
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-xs text-gray-500">Upload Image</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleMainImageChange}
                        className="hidden"
                      />
                    </label>
                    {product.main_image && (
                      <div className="relative">
                        <img
                          src={URL.createObjectURL(product.main_image)}
                          alt="Main product"
                          className="w-32 h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => setProduct(prev => ({ ...prev, main_image: null }))}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <FiX className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                  {errors.main_image && (
                    <p className="mt-1 text-sm text-red-600">{errors.main_image}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Variants Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Product Variants</h2>
              <button
                type="button"
                onClick={addVariant}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                <FiPlus className="w-4 h-4" />
                <span>Add Variant</span>
              </button>
            </div>

            {product.variants.map((variant, variantIndex) => (
              <div key={variantIndex} className="border border-gray-200 rounded-lg p-6 mb-6 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Variant {variantIndex + 1}</h3>
                  {product.variants.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeVariant(variantIndex)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {/* Variant Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Variant Title *
                    </label>
                    <input
                      type="text"
                      value={variant.title}
                      onChange={(e) => handleVariantChange(variantIndex, 'title', e.target.value)}
                      required
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`variant_${variantIndex}_title`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="e.g., Red / XL"
                    />
                    {errors[`variant_${variantIndex}_title`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`variant_${variantIndex}_title`]}</p>
                    )}
                  </div>

                  {/* Attributes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attributes *
                    </label>
                    <input
                      type="text"
                      value={variant.attributes}
                      onChange={(e) => handleVariantChange(variantIndex, 'attributes', e.target.value)}
                      required
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`variant_${variantIndex}_attributes`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="e.g., Red / XL"
                    />
                    {errors[`variant_${variantIndex}_attributes`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`variant_${variantIndex}_attributes`]}</p>
                    )}
                  </div>

                  {/* Warehouse Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Warehouse *
                    </label>
                    <select
                      value={variant.warehouse_id}
                      onChange={(e) => handleVariantChange(variantIndex, 'warehouse_id', e.target.value)}
                      required
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`variant_${variantIndex}_warehouse`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Select Warehouse</option>
                      {warehouses.map(warehouse => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name} - {warehouse.city}
                        </option>
                      ))}
                    </select>
                    {errors[`variant_${variantIndex}_warehouse`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`variant_${variantIndex}_warehouse`]}</p>
                    )}
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency
                    </label>
                    <select
                      value={variant.currency}
                      onChange={(e) => handleVariantChange(variantIndex, 'currency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>

                  {/* GST */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GST (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={variant.gst}
                      onChange={(e) => handleVariantChange(variantIndex, 'gst', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="18"
                    />
                  </div>

                  {/* MRP */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      MRP
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={variant.mrp}
                      onChange={(e) => handleVariantChange(variantIndex, 'mrp', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="110.00"
                    />
                  </div>

                  {/* Selling Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Selling Price *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={variant.price}
                      onChange={(e) => handleVariantChange(variantIndex, 'price', e.target.value)}
                      required
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`variant_${variantIndex}_price`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="100.50"
                    />
                    {errors[`variant_${variantIndex}_price`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`variant_${variantIndex}_price`]}</p>
                    )}
                  </div>

                  {/* Discount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={variant.discount}
                      onChange={(e) => handleVariantChange(variantIndex, 'discount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="10"
                    />
                  </div>

                  {/* Stock Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stock Quantity *
                    </label>
                    <input
                      type="number"
                      value={variant.stock}
                      onChange={(e) => handleVariantChange(variantIndex, 'stock', e.target.value)}
                      required
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`variant_${variantIndex}_stock`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="10"
                    />
                    {errors[`variant_${variantIndex}_stock`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`variant_${variantIndex}_stock`]}</p>
                    )}
                  </div>

                  {/* Weight */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={variant.weight}
                      onChange={(e) => handleVariantChange(variantIndex, 'weight', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="1.2"
                    />
                  </div>

                  {/* Length */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Length (cm)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={variant.length}
                      onChange={(e) => handleVariantChange(variantIndex, 'length', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="10"
                    />
                  </div>

                  {/* Width */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Width (cm)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={variant.width}
                      onChange={(e) => handleVariantChange(variantIndex, 'width', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="5"
                    />
                  </div>

                  {/* Height */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={variant.height}
                      onChange={(e) => handleVariantChange(variantIndex, 'height', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="2"
                    />
                  </div>
                </div>

                {/* Variant Images */}
                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-md font-medium text-gray-900">Variant Images</h4>
                    <button
                      type="button"
                      onClick={() => addVariantImage(variantIndex)}
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <FiPlus className="w-4 h-4" />
                      <span className="text-sm">Add Image</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {variant.images.map((image, imageIndex) => (
                      <div key={imageIndex} className="border border-gray-200 rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-sm font-medium text-gray-700">Image {imageIndex + 1}</span>
                          {variant.images.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeVariantImage(variantIndex, imageIndex)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              <FiX className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        <div className="space-y-3">
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                            {image.image ? (
                              <img
                                src={URL.createObjectURL(image.image)}
                                alt="Preview"
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center">
                                <FiImage className="w-8 h-8 text-gray-400 mb-2" />
                                <p className="text-xs text-gray-500">Upload Image</p>
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleVariantImageChange(
                                    variantIndex,
                                    imageIndex,
                                    'image',
                                    file,
                                    file.name
                                  );
                                }
                              }}
                              className="hidden"
                            />
                          </label>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Alt Text *
                            </label>
                            <input
                              type="text"
                              value={image.alt_text}
                              onChange={(e) => handleVariantImageChange(
                                variantIndex,
                                imageIndex,
                                'alt_text',
                                e.target.value
                              )}
                              className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors[`variant_${variantIndex}_alt_${imageIndex}`] ? 'border-red-500' : 'border-gray-300'
                                }`}
                              placeholder="Image description"
                            />
                            {errors[`variant_${variantIndex}_alt_${imageIndex}`] && (
                              <p className="mt-1 text-xs text-red-600">{errors[`variant_${variantIndex}_alt_${imageIndex}`]}</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">
                              Alt text is automatically generated from filename but can be edited
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              className="px-8 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingProduct}
              className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {creatingProduct ? 'Creating Product...' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </PageView>
  );
}

export default ProductCreate;